import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertBox, Box, Button, FlexBox, Icon, Typography } from '@vp/swan';
import { useChatActions, useChatMessages } from '../contexts/ChatContext';
import { AiAvatar } from './AiAvatar';
import { ChatErrorBanner } from './ChatErrorBanner';
import { GlobalChatBriefStyles } from './GlobalStyles';
import { LandingHero } from './LandingHero';
import { MessageBubble, countParagraphs } from './MessageBubble';

// How many pixels from the bottom before the scroll button appears.
const SCROLL_BUTTON_THRESHOLD = 100;

interface ChatMessageListProps {
    onReview?: () => void;
    onCtaLinkClick?: (url: string, label: string) => void;

    focusInput?: () => void;
}

export function ChatMessageList({ onReview, onCtaLinkClick, focusInput }: ChatMessageListProps) {
    const {
        messages,
        isLoading,
        error,
        isStaleSession,
        isFatalError,
        retryCount,
        hasUserSent,
        messagesEndRef,
    } = useChatMessages();
    const { onRetry } = useChatActions();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const logRef = useRef<HTMLDivElement>(null);
    const wasLoadingRef = useRef(false);

    const [showScrollButton, setShowScrollButton] = useState(false);

    // ARIA announcement. Cannot use aria-live="polite" since last agent message isn't inserted whole.

    const [announcement, setAnnouncement] = useState('');

    useEffect(() => {
        const wasLoading = wasLoadingRef.current;

        if (isLoading && !wasLoading) {
            setAnnouncement('Agent is responding');
        } else if (!isLoading && wasLoading) {
            // By the time isLoading becomes false the new agent message is already appended; then reflect reply.

            const lastAgentText = [...messages]
                .reverse()
                .find((message) => message.type === 'text' && message.role === 'agent');
            const paragraphCount =
                lastAgentText?.type === 'text' ? countParagraphs(lastAgentText.content) : 0;

            setAnnouncement(
                paragraphCount > 1
                    ? `Response complete, ${paragraphCount} paragraphs`
                    : 'Response complete'
            );
        }
        wasLoadingRef.current = isLoading;
    }, [isLoading, messages]);

    const handleScroll = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        setShowScrollButton(distanceFromBottom > SCROLL_BUTTON_THRESHOLD);
    }, []);

    const scrollToBottom = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, []);

    // Keyboard navigation for accessibility isn't free with Swan; we need to manage focus. While this doesn't lean on
    // React state at all and is global and imperative, it is probably the cleanest solution. Alternatives are a ref
    // list (less maintainable, more code), or roving tabindex (downside: re-render per arrow pres and we must keep
    // track of state vs actual focus in sync, e.g., when user clicks on a message).

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                focusInput?.();

                return;
            }

            const articles = Array.from(
                logRef.current?.querySelectorAll<HTMLElement>('[data-chat-message]') ?? []
            );

            if (articles.length === 0) {
                return;
            }

            const currentIndex = articles.indexOf(document.activeElement as HTMLElement);

            let nextIndex: number | null = null;
            switch (event.key) {
                case 'ArrowDown':
                    nextIndex =
                        currentIndex < 0 ? 0 : Math.min(currentIndex + 1, articles.length - 1);
                    break;
                case 'ArrowUp':
                    nextIndex =
                        currentIndex < 0 ? articles.length - 1 : Math.max(currentIndex - 1, 0);
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = articles.length - 1;
                    break;
                default:
                    return;
            }

            event.preventDefault();

            articles[nextIndex]?.focus();
        },
        [focusInput]
    );

    // Component-request messages are rendered in ChatInputArea, not as bubbles
    const filtered = messages.filter((msg) => msg.type !== 'componentRequest');
    // Hero stays visible until the user actually engages — a seeded greeting
    // alone shouldn't collapse it.
    const heroCollapsed = hasUserSent;

    return (
        <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                position: 'relative',
                backgroundColor: 'var(--swan-sem-color-bg-strong)',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}
            className="hide-scrollbar"
        >
            <Box
                ref={logRef}
                role="log"
                // Streaming content must not be auto-read word-by-word. Users navigate messages at their own pace.
                aria-live="off"
                aria-label="Chat messages"
                onKeyDown={handleKeyDown}
                style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                    paddingTop: 'var(--swan-sem-space-6)',
                    paddingBottom: 'var(--swan-sem-space-6)',
                    paddingLeft: 'var(--swan-sem-space-5)',
                    paddingRight: 'var(--swan-sem-space-5)',
                }}
            >
                {/* Landing hero — fades out once messages exist */}
                <Box
                    style={{
                        opacity: heroCollapsed ? 0 : 1,
                        transition: 'opacity 300ms ease-in-out',
                        pointerEvents: heroCollapsed ? 'none' : 'auto',
                        position: heroCollapsed ? 'absolute' : 'relative',
                        ...(heroCollapsed ? { height: 0, overflow: 'hidden' } : {}),
                    }}
                >
                    <GlobalChatBriefStyles />
                    <LandingHero />
                </Box>

                {filtered.map((message, index) => (
                    <MessageBubble
                        key={index}
                        message={message}
                        animate={index === filtered.length - 1 && message.role !== 'user'}
                        onCtaLinkClick={onCtaLinkClick}
                    />
                ))}

                {isLoading && (
                    <FlexBox
                        justifyContent="flex-start"
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                        aria-hidden="true"
                    >
                        <AiAvatar animated />
                    </FlexBox>
                )}

                {!isLoading && isFatalError && error && (
                    <AlertBox
                        skin="error"
                        toast={false}
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                    >
                        <Typography>{error}</Typography>
                    </AlertBox>
                )}

                {!isLoading && isStaleSession && (
                    <AlertBox
                        skin="warning"
                        toast={false}
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                    >
                        <FlexBox alignItems="center" justifyContent="space-between">
                            <Typography>
                                Your chat session is out of date. Please refresh the page to
                                proceed.
                            </Typography>
                            <Button
                                skin="link"
                                iconPosition="left"
                                onClick={() => window.location.reload()}
                            >
                                <Icon iconType="reset" size="small" mr="3" />
                                Refresh page
                            </Button>
                        </FlexBox>
                    </AlertBox>
                )}

                {!isLoading && !isFatalError && !isStaleSession && error && (
                    <ChatErrorBanner
                        retryCount={retryCount}
                        onRetry={onRetry}
                        onReview={onReview}
                    />
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Dedicated status region: announces the reply lifecycle. Kept separate from (and after) the log so its
                polite live semantics aren't affected by the log's aria-live="off". */}

            <div role="status" aria-live="polite" className="sr-only">
                {announcement}
            </div>

            <button
                type="button"
                onClick={scrollToBottom}
                title="Scroll to latest messages"
                aria-hidden="true"
                tabIndex={-1}
                style={{
                    position: 'sticky',
                    bottom: 'var(--swan-sem-space-5)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    padding: 0,
                    borderRadius: 'var(--swan-sem-border-radius-standard)',
                    border: '1px solid var(--swan-sem-color-border-standard)',
                    backgroundColor: 'var(--swan-sem-color-bg-standard)',
                    cursor: 'pointer',
                    opacity: showScrollButton ? 1 : 0,
                    transition: 'opacity 200ms ease-in-out',
                    pointerEvents: showScrollButton ? 'auto' : 'none',
                    zIndex: 10,
                }}
            >
                <Icon iconType="arrowDown" size="20p" />
            </button>
        </Box>
    );
}
